import { LitElement, html, css } from 'lit';

export class CrusherEmptyState extends LitElement {
  static properties = {
    heading: { type: String },
    message: { type: String },
    icon: { type: String },
    compact: { type: Boolean, reflect: true },
  };

  constructor() {
    super();
    this.heading = 'Nothing to show';
    this.message = '';
    this.icon = '';
    this.compact = false;
  }

  static styles = css`
    :host {
      display: block;
      min-inline-size: 0;
    }

    :host([hidden]) {
      display: none !important;
    }

    .empty {
      align-items: center;
      background: var(--crusher-empty-state-bg, color-mix(in srgb, var(--crusher-surface) 94%, transparent));
      border: var(--crusher-empty-state-border-width, var(--crusher-component-border-weight)) solid var(--crusher-empty-state-border, color-mix(in srgb, var(--crusher-border) 82%, transparent));
      border-radius: var(--crusher-empty-state-radius, var(--crusher-radius-xl));
      box-shadow: var(--crusher-empty-state-shadow, none);
      color: var(--crusher-empty-state-fg, var(--crusher-fg));
      display: grid;
      gap: var(--crusher-empty-state-gap, var(--crusher-spacing-4));
      justify-items: center;
      min-block-size: var(--crusher-empty-state-min-height, calc(var(--crusher-spacing-12) * 4));
      padding: var(--crusher-empty-state-padding, var(--crusher-spacing-10));
      text-align: center;
    }

    :host([compact]) .empty {
      min-block-size: var(--crusher-empty-state-compact-min-height, calc(var(--crusher-spacing-12) * 2));
      padding: var(--crusher-empty-state-compact-padding, var(--crusher-spacing-6));
    }

    .icon {
      align-items: center;
      background: var(--crusher-empty-state-icon-bg, color-mix(in srgb, var(--crusher-color-brand-primary) 12%, transparent));
      border: var(--crusher-empty-state-icon-border-width, var(--crusher-component-border-weight)) solid var(--crusher-empty-state-icon-border, color-mix(in srgb, var(--crusher-color-brand-primary) 28%, transparent));
      border-radius: var(--crusher-empty-state-icon-radius, var(--crusher-radius-full));
      color: var(--crusher-empty-state-icon-color, var(--crusher-color-brand-primary));
      display: inline-flex;
      font-size: var(--crusher-empty-state-icon-size, var(--crusher-font-size-2xl));
      inline-size: var(--crusher-empty-state-icon-box, var(--crusher-spacing-12));
      justify-content: center;
      min-block-size: var(--crusher-empty-state-icon-box, var(--crusher-spacing-12));
    }

    .content {
      display: grid;
      gap: var(--crusher-empty-state-content-gap, var(--crusher-spacing-2));
      justify-items: center;
      max-inline-size: var(--crusher-empty-state-content-width, calc(var(--crusher-spacing-12) * 10));
    }

    h2 {
      color: var(--crusher-empty-state-heading-color, var(--crusher-fg));
      font-size: var(--crusher-empty-state-heading-size, var(--crusher-font-size-xl));
      font-weight: var(--crusher-empty-state-heading-weight, var(--crusher-font-weight-semibold));
      line-height: var(--crusher-empty-state-heading-line-height, var(--crusher-line-height-tight));
      margin: 0;
    }

    p {
      color: var(--crusher-empty-state-message-color, var(--crusher-fg-muted));
      font-size: var(--crusher-empty-state-message-size, var(--crusher-font-size-base));
      line-height: var(--crusher-empty-state-message-line-height, var(--crusher-line-height-relaxed));
      margin: 0;
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--crusher-empty-state-action-gap, var(--crusher-spacing-3));
      justify-content: center;
    }

    .actions:empty {
      display: none;
    }
  `;

  render() {
    return html`
      <section class="empty" part="base" aria-label=${this.heading || 'Empty state'}>
        ${this.icon ? html`<div class="icon" part="icon" aria-hidden="true">${this.icon}</div>` : html`<slot name="icon"></slot>`}
        <div class="content" part="content">
          <h2 part="heading">${this.heading}</h2>
          ${this.message ? html`<p part="message">${this.message}</p>` : html`<slot name="message"></slot>`}
        </div>
        <div class="actions" part="actions">
          <slot name="actions"></slot>
        </div>
      </section>
    `;
  }
}

customElements.define('crusher-empty-state', CrusherEmptyState);
